import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip, Drawer, Grid} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDepartmentsList, deleteDepartment } from "../departmentsThunks";
import { App } from "antd";
import { selectDepartmentsList } from "../departmentsSelectors";
import {
  setSelectedDepartment,
  setIsEditDepartment,
} from "../departmentsSlice";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const DepartmentList = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  let [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [helpOpen, setHelpOpen] = useState(false);
   const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    const access = getAccessType("department_entry");
    access_type_set(access);
  }, []);

  const departmentsList = useSelector(selectDepartmentsList);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const handleDelete = async (department_id, name) => {
    modal.confirm({
      title: "Are you sure?",
      content: `This will delete department: ${name}`,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteDepartment(department_id)).unwrap();
          modal.success({ title: "Department deleted successfully" });
          dispatch(fetchDepartmentsList());
        } catch (error) {
          modal.warning({ title: error.message || "Delete failed" });
        }
      },
    });
  };

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Department Name",
      dataIndex: "department_name",
      sorter: (a, b) => a.name?.localeCompare(b.name),
    },
    ...(access_type === "WRITE"
      ? [
          {
            title: "Actions",
            render: (_, record) => (
              <>
                <Button
                  type="link"
                 disabled={!(isAdminUser || canEdit)}
                  onClick={() => {
                    dispatch(setSelectedDepartment(record));
                    dispatch(setIsEditDepartment(true));
                    navigate("addNewDepartment");
                  }}
                  icon={<EditOutlined />}
                />
                <Button
                  type="link"
                  danger
                  disabled={!(isAdminUser || canDelete)}
                  onClick={() =>
                    handleDelete(record.department_id, record.department_name)
                  }
                  icon={<DeleteOutlined />}
                />
              </>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    dispatch(fetchDepartmentsList());
    dispatch(setSelectedDepartment(null));
    dispatch(setIsEditDepartment(false));
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                className="traic-button"
                icon={<PlusOutlined />}
                style={{
                  minWidth: "112px",
                  height: "36px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => {
                  dispatch(setSelectedDepartment(null));
                  dispatch(setIsEditDepartment(false));
                  navigate("addNewDepartment");
                }}
                data-testid="add-department-btn"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={departmentsList}
        rowKey="department_id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Department Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Department Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Department module is used to create and manage company departments
            within the ERP system. Departments help organize employees,
            workflows, reporting structures, and operational responsibilities.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View existing department records</li>
              <li>Use <strong>Add New</strong> to create departments</li>
              <li>Edit department details when required</li>
              <li>Delete departments if permitted</li>
              <li>Organize employee and operational structures</li>
              <li>Manage department-based reporting and workflows</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Department names should remain unique.</li>
              <li>Deleting departments may affect linked employee records.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Department organization improves operational management.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default DepartmentList;
